<template>
  <div class="auth-form w-100">
    <template v-if="!hasValidQuery">
      <p class="text-body-2 text-medium-emphasis mb-4">
        El enlace no es válido o ha expirado. Solicite un nuevo restablecimiento de contraseña desde el inicio de sesión.
      </p>
      <v-btn block color="primary" :to="{ path: '/login' }">
        Ir al inicio de sesión
      </v-btn>
    </template>

    <template v-else>
      <h1 class="auth-form__title text-h5 font-weight-semibold mb-2">
        Nueva contraseña
      </h1>
      <p class="text-body-2 text-medium-emphasis mb-6">
        Ingrese y confirme su nueva contraseña para finalizar el restablecimiento.
      </p>

      <v-form ref="authForm" @submit.prevent="handleSubmit">
        <div class="stack-fields">
          <v-text-field
            v-model="password"
            label="Contraseña"
            placeholder="Ingrese su nueva contraseña"
            :type="showPassword ? 'text' : 'password'"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :rules="[rules.required, rules.passwordPolicy]"
            autocomplete="off"
            :append-inner-icon="showPassword ? eyeOffIcon : eyeIcon"
            @click:append-inner="showPassword = !showPassword"
          />

          <PasswordPolicyChecklist :password="password" />

          <v-text-field
            v-model="confirmPassword"
            label="Repita su contraseña"
            placeholder="Repita su contraseña"
            :type="showConfirmPassword ? 'text' : 'password'"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :rules="[rules.required, rules.sameAs(password)]"
            autocomplete="off"
            :append-inner-icon="showConfirmPassword ? eyeOffIcon : eyeIcon"
            @click:append-inner="showConfirmPassword = !showConfirmPassword"
          />

          <div class="d-flex flex-wrap gap-3 mt-4">
            <v-btn variant="outlined" type="button" :to="{ path: '/login' }">
              Cancelar
            </v-btn>
            <v-btn
              type="submit"
              color="primary"
              class="flex-grow-1"
              min-width="120"
              :loading="isLoading"
            >
              {{ isLoading ? 'Guardando...' : 'Confirmar' }}
            </v-btn>
          </div>
        </div>
      </v-form>
    </template>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import enums from '../../utils/enums'
import rules from '../../utils/helpers/rules'
import PasswordPolicyChecklist from './PasswordPolicyChecklist.vue'

export default {
  name: 'RestablecerContrasenaForm',
  components: { PasswordPolicyChecklist },
  props: {
    hospitalConfig: {
      type: Object,
      required: true,
    },
    /** Desde query: userId */
    userId: {
      type: String,
      default: '',
    },
    /** Desde query: token (reset token) */
    token: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      eyeIcon: enums.icons.EYE,
      eyeOffIcon: enums.icons.EYE_OFF,
      password: '',
      confirmPassword: '',
      showPassword: false,
      showConfirmPassword: false,
      isLoading: false,
      rules,
    }
  },
  computed: {
    hasValidQuery() {
      const u = String(this.userId ?? '').trim()
      const t = String(this.token ?? '').trim()
      return u.length > 0 && t.length > 0
    },
  },
  methods: {
    ...mapActions({
      setAlert: 'alerts/setAlert',
      restablecerPassword: 'auth/restablecerPassword',
    }),
    async handleSubmit() {
      const { valid } = await this.$refs.authForm.validate()
      if (!valid) return
      if (this.password !== this.confirmPassword) {
        this.setAlert({
          type: 'warning',
          message: 'Las contraseñas no coinciden',
        })
        return
      }
      this.isLoading = true
      const payload = {
        userId: this.userId,
        token: this.token,
        newPassword: this.password,
      }
      try {
        const result = await this.restablecerPassword(payload)
        if (result?.ok) {
          this.setAlert({
            type: 'success',
            message: 'Su contraseña fue actualizada éxitosamente. Ya puede iniciar sesión.',
          })
          await this.$router.replace({ path: '/login' })
        }
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>
.auth-form {
  max-width: 28rem;
}
.auth-form__title {
  color: var(--portal-primary);
}
.stack-fields > * + * {
  margin-top: 1.25rem;
}
.gap-3 {
  gap: 0.75rem;
}
</style>
